/**
 * Module Staff #06 — Audit factures & conformité OHADA
 * Source : Guide_Admin_Baobab_Connect.pdf · Chapitre 5
 */
const moduleStaffAuditFactures = {
  id: 'staff-audit-factures',
  population: 'staff',
  order: 6,
  category: 'Finance',
  title: 'Audit factures & OHADA',
  subtitle: 'Détecter doublons, factures legacy et runs orphelins · Garantir la conformité',
  icon: 'Shield',
  duration: '25 min',
  level: 'Avancé',
  audience: 'CFO, contrôleurs, comptables',
  pdfUrl: '/api/uploads/docs/training/staff-audit-factures.pdf',
  slides: [
    { type: 'cover', eyebrow: 'Module 06 — Staff', title: 'Audit factures & OHADA', subtitle: 'Conformité comptable + qualité des données financières', icon: 'Shield', duration: '25 min', audience: 'Direction financière', level: 'Avancé' },
    { type: 'intro', title: 'Objectifs', intro: 'L\'audit factures est le garant de la fiabilité des KPIs financiers et de la conformité OHADA. Ce module vous donne tous les leviers pour piloter la qualité.', objectives: [
      'Comprendre les 3 axes d\'audit : doublons, légacy/dates, runs orphelins',
      'Détecter une facture sans nom de société (companyName manquant) et la backfill',
      'Identifier et régénérer les factures à l\'ancien format (N°00xxxxxx → FACT-YYYY-MM-seq)',
      'Aligner la date d\'émission sur la date d\'opération réelle',
      'Utiliser la sélection partielle (checkboxes) pour traiter par lots',
      'Connaître les obligations OHADA et les conséquences d\'une non-conformité',
    ]},
    { type: 'concept', eyebrow: 'Pourquoi auditer ?', icon: 'AlertTriangle', title: 'Les enjeux concrets',
      lede: 'Une facture mal renseignée peut coûter cher :',
      bullets: [
        '⚖️ OHADA : numérotation séquentielle obligatoire, sinon nullité de la pièce',
        '📊 KPIs faussés : 1 facture sans companyName masque le vrai CA par client',
        '💸 Doublons : 2 factures pour une opération → contestation client garantie',
        '🔍 Audit DGI : pénalités si la séquence est cassée ou les dates désalignées',
        '⏱️ DSO erroné : si la date facture ≠ date opération, les retards sont mal calculés',
      ],
      note: 'Tous ces problèmes sont détectés automatiquement par le moteur d\'audit, vous n\'avez qu\'à les traiter.',
    },
    { type: 'screenshot', eyebrow: 'Vue d\'ensemble', title: 'La page Audit Factures',
      callouts: [
        { label: 'Section "Factures inconnu"', text: 'Factures avec companyName manquant — backfill automatique disponible.', x: 25, y: 18 },
        { label: 'Section "Factures legacy"', text: 'Anciens numéros N°00xxxxxx à régénérer en FACT-AAAA-MM-NNN.', x: 25, y: 45 },
        { label: 'Sélection partielle', text: 'Checkboxes pour traiter par lot — recommandé en production.', x: 50, y: 60 },
        { label: 'Bouton "Régénérer la sélection"', text: 'Renumérote selon la date d\'opération réelle.', x: 75, y: 75 },
        { label: 'Compteurs dynamiques', text: 'Mise à jour temps réel après chaque action.', x: 90, y: 25 },
      ],
    },
    { type: 'concept', eyebrow: 'Les 3 axes', icon: 'BookOpen', title: 'Les 3 axes d\'audit en détail',
      lede: 'Le moteur d\'audit balaye votre base sous 3 angles complémentaires :',
      bullets: [
        '🔁 Doublons : 2+ factures pour une même opération (clé : transport_request_id).',
        '🏢 Inconnu : companyName vide → masque le vrai client dans les rapports.',
        '📅 Legacy & dates : ancien format ou date d\'émission désalignée de l\'opération.',
      ],
      note: 'Chaque axe a son propre bloc UI avec compteur et actions de masse.',
    },
    { type: 'steps', eyebrow: 'Workflow standard', title: 'Backfill des factures "inconnu" (entreprise manquante)', steps: [
      { title: 'Ouvrir Audit Factures', detail: 'Sidebar → Finance → Audit Factures.' },
      { title: 'Section "Factures inconnu"', detail: 'Compteur affiché : ex. "5 factures à traiter".' },
      { title: 'Bouton "Backfill automatique"', detail: 'Le système croise avec les transport_requests pour retrouver le client.' },
      { title: 'Vérification', detail: 'Les 5 factures passent en "Résolues" avec le companyName remis.' },
      { title: 'Garde permanente', detail: 'Un mécanisme empêche désormais la création de factures sans companyName.' },
    ]},
    { type: 'steps', eyebrow: 'Action critique', title: 'Régénérer des factures legacy en bulk', steps: [
      { title: 'Identifier la section', detail: '"Factures legacy & dates désalignées" — généralement les anciennes factures importées.' },
      { title: 'Filtrer si besoin', detail: 'Par période, par client, par montant.' },
      { title: 'Cocher les factures à traiter', detail: 'Sélection partielle (recommandé : par lot de 50).' },
      { title: 'Bouton "Régénérer la sélection"', detail: 'Confirmation avec récap : ancien numéro → nouveau numéro.' },
      { title: 'Le numéro est anchored sur la date d\'opération', detail: 'FACT-AAAA-MM-NNN où AAAA-MM = mois de l\'opération réelle.' },
      { title: 'Vérification post-régen', detail: 'Les KPIs financiers se mettent à jour automatiquement.' },
    ]},
    { type: 'concept', eyebrow: 'Conformité', icon: 'Shield', title: 'Obligations OHADA en bref',
      lede: 'Les règles fondamentales de la facturation OHADA :',
      bullets: [
        '📋 Numérotation séquentielle, sans trou ni doublon (audit DGI).',
        '🗓️ Date d\'émission = date de la prestation (ou facturation, mais alignée).',
        '🏢 Identification complète : NCC, RC, adresse, contact.',
        '🔢 Mentions obligatoires : numéro facture, HT, TVA, TTC, échéance.',
        '📁 Conservation : 10 ans minimum.',
        '✍️ Avoir : numéro propre (AVR-AAAA-MM-NNN) et lien vers la facture corrigée.',
      ],
      note: 'Baobab Connect garantit toutes ces obligations nativement — votre rôle est d\'auditer les anomalies historiques.',
    },
    { type: 'screenshot', eyebrow: 'Cas concret', title: 'Régénération de 62 factures legacy (exemple réel)',
      callouts: [
        { label: 'Avant', text: '62 factures au format N°00267xxx, dates incohérentes.', x: 25, y: 25 },
        { label: 'Pendant', text: 'Bulk regen lancé via sélection partielle (lots de 20).', x: 50, y: 45 },
        { label: 'Après', text: '62 factures renumérotées en FACT-AAAA-MM-NNN, KPIs alignés.', x: 75, y: 65 },
      ],
    },
    { type: 'checklist', title: 'Checklist mensuelle audit (J-2 avant clôture)', items: [
      'Lancer l\'audit complet via le bouton "Auditer maintenant"',
      'Traiter les factures "inconnu" via backfill automatique',
      'Régénérer les factures legacy par lots',
      'Vérifier les doublons et trancher (garder la bonne / annuler l\'autre)',
      'Confirmer l\'alignement date opération ↔ date facture',
      'Lancer la régénération PDF pour les factures modifiées',
      'Documenter les actions dans le journal d\'audit',
    ]},
    { type: 'comparison', title: 'Audit : bonnes pratiques vs pièges', do: [
      'Traiter par lots pour valider chaque batch (sélection partielle)',
      'Vérifier les KPIs avant/après régénération',
      'Documenter les actions correctives dans un fichier de suivi',
      'Régénérer les PDFs physiques après changement de numéros',
      'Activer la garde permanente pour bloquer les futures incohérences',
    ], dont: [
      'Ne lancez jamais un bulk regen complet sans dry-run préalable',
      'Ne supprimez jamais une facture en doublon — annulez-la',
      'Ne modifiez pas un numéro manuellement (corruption séquentielle)',
      'N\'oubliez pas la régénération du PDF physique (sinon désynchro)',
      'Ne contournez pas la garde permanente "même en urgence"',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Vous trouvez 47 factures au format N°00xxxxxx. Quelle méthode privilégier ?',
      options: [
        'Modifier manuellement chaque facture',
        'Sélectionner les 47, lancer un bulk regen anchored sur la date d\'opération',
        'Supprimer les 47 et recréer',
        'Demander à la DSI de modifier la base',
      ],
      answerIndex: 1,
      explanation: 'La sélection partielle + bulk regen est la méthode officielle. Elle garantit l\'anchoring sur la date d\'opération et la conformité OHADA.',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Après régénération des numéros, que reste-t-il à faire pour la conformité physique ?',
      options: [
        'Rien, le système gère tout',
        'Régénérer le PDF physique de chaque facture (sinon désynchro DB/PDF)',
        'Envoyer un email à tous les clients concernés',
        'Annuler les anciens PDFs et recréer manuellement',
      ],
      answerIndex: 1,
      explanation: 'La régénération en DB met à jour le numéro et la date, mais le PDF physique stocké reste avec l\'ancien numéro. Le bulk regen PDF doit suivre.',
    },
    { type: 'quiz', title: 'Quiz #3',
      question: 'Pourquoi la "garde permanente" sur _generate_invoice_number est cruciale ?',
      options: [
        'Pour bloquer la création de nouvelles incohérences à la source',
        'Pour ralentir le système',
        'Pour empêcher la suppression de factures',
        'Pour générer plus de revenus',
      ],
      answerIndex: 0,
      explanation: 'La garde empêche désormais qu\'une facture créée n\'ait un numéro désaligné de sa date d\'opération. C\'est de la prévention, pas de la correction.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      'Audit Factures = qualité données + conformité OHADA en 1 page.',
      'Backfill auto pour "inconnu" + Bulk regen pour legacy = 2 actions clés.',
      'Toujours faire la régénération PDF physique après la DB.',
      'Sélection partielle = sécurité ; jamais de bulk total sans dry-run.',
      'La garde permanente prévient au lieu de corriger après coup.',
    ]},
    { type: 'cta', title: 'Module 06 terminé !', subtitle: 'Vous êtes garant de la conformité financière. Mettez en pratique dès aujourd\'hui.',
      actions: [
        { label: 'Ouvrir Audit Factures', href: '/admin/audit-factures' },
        { label: 'Module Cockpit CFO', href: '/docs/admin/training/staff-cockpit-cfo' },
        { label: 'Catalogue', href: '/docs/admin/training' },
      ],
    },
  ],
};

export default moduleStaffAuditFactures;
